import PropTypes from 'prop-types';
import React from 'react';
import api from '../../../Api';
import makeConnectTo from '../../util/makeConnectTo';
import VoteType from '../VoteType';
import { connectToCollaborativeLists } from './CollaborativeListsDataStore';
import { message, Modal } from 'antd';
import InputType from '../InputType';
import TitlesAddedSummary from '../TitlesAddedSummary';
import { isIsbn } from '../../../util/validate';
import { isFunction, partition } from 'lodash-es';
import ListNotificationModalContent from '../Administer/ListNotificationModalContent';
import { connectToUser } from '../../User/containers/UserDataStore';
import combineConnectors from '../../util/combineConnectors';
import { TitleListSort } from '../TitleListSort';
import { withLocalize } from '../../Localization/withLocalize';

const CollaborativeListContext = React.createContext();

export const useCollaborativeList = () =>
	React.useContext(CollaborativeListContext);

const name = 'collaborativeListDataStore';

export const connectToCollaborativeList = makeConnectTo(
	CollaborativeListContext,
	name,
);

export class CollaborativeListDataStore extends React.Component {
	static propTypes = {
		children: PropTypes.oneOfType([PropTypes.element, PropTypes.func]),
		translate: PropTypes.func,
	};

	state = {
		collaborativeListData: {},
		alerts: [],
		isLoading: true,
		areAlertsLoading: true,
		skus: {},
		conversations: {},
		areConversationsLoading: true,
	};

	render() {
		const collaborativeListDataStore = this.getStore();
		return (
			<CollaborativeListContext.Provider
				value={collaborativeListDataStore}>
				{typeof this.props.children === 'function'
					? this.props.children(collaborativeListDataStore)
					: this.props.children}
			</CollaborativeListContext.Provider>
		);
	}

	componentDidMount() {
		this.fetchCollaborativeList();
		this.fetchAlerts();
	}

	componentWillUnmount() {
		if (this.collaborativeListFetch && this.collaborativeListFetch.cancel) {
			this.collaborativeListFetch.cancel();
		}
		if (this.alertsFetch && this.alertsFetch.cancel) {
			this.alertsFetch.cancel();
		}
		if (this.conversationsFetch && this.conversationsFetch.cancel) {
			this.conversationsFetch.cancel();
		}
	}

	getStore = (state = this.state) => ({
		state,
		refresh: () => this.refreshCollaborativeList(),
		createNewQuantity: values => this.createNewQuantity(values),
		updateQuantity: values => this.updateQuantity(values),
		deleteQuantity: values => this.deleteQuantity(values),
		createNewComment: values => this.createNewComment(values),
		updateComment: values => this.updateComment(values),
		deleteComment: commentId => this.deleteComment(commentId),
		addTitle: sku => this.addTitle(sku),
		removeTitle: sku => this.removeTitle(sku),
		archiveList: () => this.archiveList(),
		createNewAlert: alert => this.createNewAlert(alert),
		deleteAlert: alertId => this.deleteAlert(alertId),
		updateAlert: (alert, alertAttribute) =>
			this.updateAlert(alert, alertAttribute),
		addTitlesToList: skus => this.addTitlesToList(skus),
		postTitlesToList: (listId, titles, onSuccess, onFail) =>
			this.postTitlesToList(listId, titles, onSuccess, onFail),
		refreshCollabLists: () => this.refreshCollabLists(),
		fetchConversationStatus: () => this.fetchConversationStatus(),
		toggleVote: (vote, voteType, onSuccess) =>
			this.toggleVote(vote, voteType, onSuccess),
	});

	handleNotificationPrompt = () => {
		const { listId } = this.props;
		const listsUserHasSeenNotificationModalFor = JSON.parse(
			localStorage.getItem('listsUserHasSeenNotificationModalFor'),
		);
		const setItemsToLS = itemsToSave =>
			localStorage.setItem(
				'listsUserHasSeenNotificationModalFor',
				JSON.stringify(itemsToSave),
			);
		const hasSeenListModals = listsUserHasSeenNotificationModalFor || false;
		if (hasSeenListModals) {
			const hasSeenThisListModal = listsUserHasSeenNotificationModalFor.includes(
				listId,
			);
			!hasSeenThisListModal &&
				listsUserHasSeenNotificationModalFor.push(listId);
			!hasSeenThisListModal && this.showListNotificationModal();
			setItemsToLS(listsUserHasSeenNotificationModalFor);
		} else {
			setItemsToLS([listId.toString()]);
			this.showListNotificationModal();
		}
	};

	showListNotificationModal = () => {
		const { communityId, listId } = this.props;
		Modal.info({
			className: 'notification-prompt-modal',
			title: this.props.translate('manage_list_notifications'),
			content: (
				<ListNotificationModalContent
					listId={listId}
					isVerticalView={true}
					communityId={communityId}
					translate={this.props.translate}
				/>
			),
			okText: this.props.translate('ok'),
			onOk: this.refreshAlerts,
		});
	};

	refreshCollabLists = () => {
		this.props.collaborativeListsDataStore &&
			this.props.collaborativeListsDataStore.refresh();
	};

	refreshCollaborativeList = () => {
		this.fetchCollaborativeList();
		return this.collaborativeListFetch.promise;
	};

	fetchCollaborativeList = () => {
		const { communityId, listId, userDataStore } = this.props;
		const options =
			userDataStore && userDataStore.isAnonymous()
				? 'collaborativeTitleListItems,collaborativeTitleListVotes,collaborativeTitleListComments,collaborativeTitleListQuantities,productInfo,userInfo,userAvatars'
				: '';
		const request = api.cancelableGet(
			`/api/v1/collaborativeTitleLists/${communityId}/${listId}/items/${options}`,
		);
		request.promise.then(this.cacheApiResponse);
		this.collaborativeListFetch = request;
	};

	cacheApiResponse = apiResponse => {
		const collaborativeListData = this.getCollaborativeListData(
			apiResponse,
		);
		this.setState({
			collaborativeListData,
			isLoading: false,
			skus: this.getSkus(collaborativeListData.listItems),
		});
		this.fetchConversationStatus();
	};

	getSkus = titles => {
		return titles && titles.map(item => item.sku);
	};

	getCollaborativeListData = apiResponse => {
		const r = apiResponse;
		const list = r.lists[0];
		const { sort } = list;
		return {
			list,
			listItems: this.mergeData(
				r.listItems,
				r.products,
				r.votes,
				r.comments,
				r.quantities,
				r.downloadableSkus,
				r.requestableSkus,
				r.digitalCopyInfo,
			).sort(function(x, y) {
				const xSequence = x.sequence || 0;
				const ySequence = y.sequence || 0;

				if (sort === TitleListSort.Votes) {
					return y.netVotes - x.netVotes || xSequence - ySequence;
				}

				return xSequence - ySequence;
			}),
			logHistory: this.getLogHistory(
				r.votes,
				r.comments,
				r.quantities,
				r.listItems,
			).sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate)),
			userAvatarsByAppUserId: r.userAvatarsByAppUserId,
			usersById: r.usersById,
			products: r.products,
			votes: r.votes,
			comments: r.comments,
			quantities: r.quantities,
		};
	};

	mergeData(
		listItems,
		products,
		votes,
		comments,
		quantities,
		downloadableSkus,
		requestableSkus,
		digitalCopyInfo,
	) {
		return listItems.map(li => ({
			...li,
			title: products.filter(pr => pr.sku === li.sku)[0].name,
			author: products.filter(pr => pr.sku === li.sku)[0].author,
			product: products.filter(pr => pr.sku === li.sku)[0],
			uri:
				products.filter(pr => pr.sku === li.sku)[0].images &&
				products
					.filter(pr => pr.sku === li.sku)[0]
					.images.filter(im => im.type === 'jacketCover').length >
					0 &&
				products
					.filter(pr => pr.sku === li.sku)[0]
					.images.filter(im => im.type === 'jacketCover')[0].uri +
					'?height=600',
			votes: votes.filter(pr => pr.sku === li.sku),
			comments: comments.filter(pr => pr.sku === li.sku),
			quantities: quantities.filter(pr => pr.sku === li.sku),
			netVotes:
				votes.filter(pr => pr.sku === li.sku && pr.type === 'upVote')
					.length -
				votes.filter(pr => pr.sku === li.sku && pr.type === 'downVote')
					.length,
			isRequestable:
				requestableSkus &&
				requestableSkus.filter(rs => rs === li.sku)[0],
			isDownloadable:
				downloadableSkus &&
				downloadableSkus.filter(dl => dl === li.sku)[0],
			availableReviewCopyFormats:
				digitalCopyInfo &&
				digitalCopyInfo.filter(dci => dci.sku === li.sku),
		}));
	}

	getLogHistory = (votes, comments, quantities, listItems) => {
		var logHistory = [];
		votes.forEach(vote => {
			logHistory.push({
				sku: vote.sku,
				appUserId: vote.appUserId,
				inputType:
					vote.type === VoteType.upVote
						? InputType.UpVote
						: InputType.DownVote,
				createdDate: vote.createdDate,
			});
		});
		comments.forEach(comment => {
			logHistory.push({
				sku: comment.sku,
				appUserId: comment.appUserId,
				inputType: InputType.Comment,
				createdDate: comment.createdDate,
			});
		});
		quantities.forEach(quantity => {
			logHistory.push({
				sku: quantity.sku,
				appUserId: quantity.appUserId,
				inputType: InputType.Quantity,
				createdDate: quantity.createdDate,
			});
		});
		listItems.forEach(item => {
			logHistory.push({
				sku: item.sku,
				appUserId: item.appUserId,
				inputType: InputType.Add,
				createdDate: item.createdDate,
			});
		});
		return logHistory;
	};

	refreshAlerts = () => {
		this.fetchAlerts();
		return this.alertsFetch.promise;
	};

	fetchAlerts = () => {
		const { listId } = this.props;
		const request = api.cancelableGet(
			`/api/v1/collaborativeTitleLists/alerts/${listId}`,
		);
		request.promise.then(this.cacheAlertsApiResponse);
		this.alertsFetch = request;
	};

	cacheAlertsApiResponse = apiResponse => {
		this.setState({ alerts: apiResponse, areAlertsLoading: false });
	};

	createNewQuantity(values) {
		values.listId = this.props.listId;
		api.post('/api/v1/collaborativeTitleList/quantities', values).then(
			() => {
				this.refreshCollaborativeList();
				message.success(<span>Quantity added!</span>);
			},
		);
	}

	updateQuantity(values) {
		api.put('/api/v1/collaborativeTitleList/quantities', values).then(
			() => {
				this.refreshCollaborativeList();
				message.success(<span>Your Quantity has been updated</span>);
			},
		);
	}

	deleteQuantity(values) {
		api.call(
			'DELETE',
			'/api/v1/collaborativeTitleList/quantities',
			values,
		).then(() => {
			this.refreshCollaborativeList();
			message.success(<span>Your Quantity input has been removed</span>);
		});
	}

	createNewComment(values) {
		api.post('/api/v1/collaborativeTitleList/comments', values)
			.then(() => {
				this.refreshCollaborativeList();
				message.success(<span>Comment added!</span>);
				!this.state.alerts.length && this.handleNotificationPrompt();
			})
			.catch(e => console.error(e));
	}

	updateComment(values) {
		api.put('/api/v1/collaborativeTitleList/comments', values).then(() => {
			this.refreshCollaborativeList();
			message.success(<span>Your Comment has been updated</span>);
		});
	}

	deleteComment(commentId) {
		api.del(`/api/v1/collaborativeTitleList/comments/${commentId}`).then(
			() => {
				this.refreshCollaborativeList();
				message.success(<span>Your Comment has been removed</span>);
			},
		);
	}

	toggleVote(vote, onSuccessOrFail) {
		const isRemovingExistingVote = vote.type === 0;
		api.post('/api/v1/votes', vote)
			.then(() => {
				this.refreshCollaborativeList();
				if (isRemovingExistingVote) {
					message.success(
						this.props.translate('your_vote_has_been_removed'),
					);
				} else {
					message.success(
						this.props.translate('your_vote_has_been_recorded'),
					);
				}
				!this.state.alerts.length && this.handleNotificationPrompt();
			})
			.catch(e => {
				console.error(e);
				message.error(
					this.props.translate('issue_with_request_message'),
				);
			})
			.finally(() => {
				if (isFunction(onSuccessOrFail)) onSuccessOrFail();
			});
	}

	checkIfTitleOnList = sku => {
		const { listItems } = this.state.collaborativeListData;
		return listItems.filter(li => li.sku.indexOf(sku) > -1).length > 0;
	};

	addTitle = sku => {
		if (!this.checkIfTitleOnList(sku)) {
			const { listId } = this.props;
			const title = [
				{
					listId: listId,
					sku: sku,
				},
			];
			const onSuccess = () => this.renderTitleAlert(sku, listId);
			this.postTitlesToList(listId, title, onSuccess);
		} else {
			message.success(this.renderTitleAlreadyOnListAlert());
		}
	};

	renderTitleAlreadyOnListAlert = () => (
		<span>
			This title is already on the list. You can upvote it to register
			your opinion.
		</span>
	);

	renderTitleAlert = async (sku, listId) => {
		let collabList = this.props.collaborativeListsDataStore.getListDetails(
			listId,
		);
		let request = api.cancelableGet(`/api/products/${sku}`);
		const product = await request.promise;
		message.success(
			<span>
				You successfully added <em>{product.name}</em> to{' '}
				{collabList.name}
			</span>,
		);
	};

	removeTitle(sku) {
		const { listId } = this.props;
		api.del(`/api/v1/collaborativeTitleLists/${listId}/items/${sku}`)
			.then(() => {
				this.refreshCollaborativeList();
				this.refreshCollabLists();
				message.success(
					<span>You've removed this title from the list</span>,
				);
			})
			.catch(err => console.log(`error`, err));
	}

	archiveList() {
		const { listId } = this.props;
		api.del(`/api/v1/collaborativeTitleLists/${listId}`).then(() => {
			this.refreshCollaborativeList();
			let collabList = this.props.collaborativeListsDataStore.getListDetails(
				listId,
			);
			message.success(
				<span>
					{collabList.name} has been archived and will not be viewable
					anymore
				</span>,
			);
		});
	}

	createNewAlert(alert) {
		this.setState({ areAlertsLoading: true });
		api.post('/api/v1/collaborativeTitleLists/alerts', alert).then(() => {
			this.refreshAlerts();
			message.success(<span>Alert added!</span>);
		});
	}

	updateAlert(alert, alertAttribute) {
		api.put(
			`/api/v1/collaborativeTitleLists/alerts/${alertAttribute}`,
			alert,
		).then(() => {
			this.refreshAlerts();
			message.success(<span>Alert updated!</span>);
		});
	}

	deleteAlert(alertId) {
		this.setState({ areAlertsLoading: true });
		api.del(`/api/v1/collaborativeTitleLists/alerts/${alertId}`).then(
			() => {
				this.refreshAlerts();
				message.success(<span>Alert removed!</span>);
			},
		);
	}

	getTitlesSummary = isbns => {
		const { listItems } = this.state.collaborativeListData;
		const currentTitleIsbns = listItems.map(li => li.product.ean);
		const [validIsbns, invalidIsbns] = partition(isbns, isIsbn);
		const [isbnsToAdd, alreadyAddedIsbns] = partition(
			validIsbns,
			isbn => !currentTitleIsbns.includes(isbn),
		);
		return { isbnsToAdd, alreadyAddedIsbns, invalidIsbns };
	};

	postTitlesToList = (listId, titles, onSuccess, onFail) => {
		api.post(`/api/v1/collaborativeTitleLists/${listId}/items/add`, titles)
			.then(() => {
				if (typeof onSuccess === 'function') onSuccess();
				this.refreshCollaborativeList();
				this.refreshCollabLists();
				!this.state.alerts.length && this.handleNotificationPrompt();
			})
			.catch(() => {
				message.error(
					this.props.translate('issue_with_request_message'),
				);
				if (typeof onFail === 'function') onFail();
			});
	};

	addTitlesToList = isbns => {
		const { listId } = this.props;
		const {
			isbnsToAdd,
			alreadyAddedIsbns,
			invalidIsbns,
		} = this.getTitlesSummary(isbns);

		const titlesToPost = isbnsToAdd.map(titleIsbn => ({
			listId: listId,
			sku: titleIsbn,
		}));

		if (isbnsToAdd.length > 0) {
			this.postTitlesToList(listId, titlesToPost);
		}
		this.renderTitlesAddedSummaryModal(
			alreadyAddedIsbns,
			isbnsToAdd,
			invalidIsbns,
		);
	};

	renderTitlesAddedSummaryModal = (
		alreadyAddedIsbns,
		successfullyAddedIsbns,
		invalidIsbns,
	) => {
		Modal.info({
			content: (
				<TitlesAddedSummary
					alreadyAddedIsbns={alreadyAddedIsbns}
					successfullyAddedIsbns={successfullyAddedIsbns}
					invalidIsbns={invalidIsbns}
				/>
			),
		});
	};

	fetchConversationStatus = () => {
		const { communityId } = this.props;
		const { skus } = this.state;

		const request = api.cancelablePost(
			`/api/v1/conversations/${communityId}/titles`,
			skus,
		);
		request.promise.then(this.cacheConversationApiResponse);
		this.conversationsFetch = request;
	};

	cacheConversationApiResponse = apiResponse => {
		this.setState({
			conversations: apiResponse,
			areConversationsLoading: false,
		});
	};
}

const connectors = [connectToCollaborativeLists, connectToUser, withLocalize];

export default combineConnectors(connectors)(CollaborativeListDataStore);
